import React, { useState, useEffect } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { errorToast, successToast } from "../../../services/toast.service";
import {
  CircularProgress,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { processGoogleCredential } from "../../../services/googleAuthService";
import "../AuthModule/styles/LoginScreen.css";

const GoogleAuthScreen = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(null);
  const [userDetails, setUserDetails] = useState({
    firm: "",
    phone: "",
    requestType: "both",
  });

  // Check if user has a valid google auth session
  useEffect(() => {
    const googleAuthToken = localStorage.getItem("google_auth_token");
    const googleRefreshToken = localStorage.getItem("google_refresh_token");

    if (googleAuthToken && googleRefreshToken) {
      // If tokens exist, assume the user is authenticated and proceed
      setIsApproved(true);
      if (onAuthSuccess) onAuthSuccess();
    }
  }, [onAuthSuccess]);

  const handleGoogleLoginSuccess = async credentialResponse => {
    setIsLoading(true);
    try {
      if (credentialResponse.credential) {
        const response = await processGoogleCredential(credentialResponse, {
          company: userDetails.firm, // Send firm name as company to backend
          phone: userDetails.phone,
          requestType: userDetails.requestType,
        });

        if (response.success) {
          // Store auth tokens
          localStorage.setItem("google_auth_token", response.data.accessToken);
          localStorage.setItem(
            "google_refresh_token",
            response.data.refreshToken,
          );
          // Store user info
          localStorage.setItem(
            "google_auth_user",
            JSON.stringify(response.user),
          );

          setIsApproved(true);
          successToast(response.message || "Google authentication successful!");
          // Call the onAuthSuccess prop to update the parent state
          if (onAuthSuccess) onAuthSuccess();
          // Redirect to the dashboard
          navigate("/");
        } else {
          // Check if this is a pending approval message
          if (
            response.message &&
            response.message.toLowerCase().includes("pending approval")
          ) {
            setIsApproved(false);
            errorToast(
              response.message || "Your access request is pending approval.",
            );
          } else {
            throw new Error(response.message || "Authentication failed");
          }
        }
      } else {
        errorToast("Google login failed. Please try again.");
      }
    } catch (error) {
      console.error("Google auth verification failed:", error);
      errorToast(error.message || "Authentication failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    errorToast("Google login failed. Please try again.");
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Get your client ID from .env file or hardcode it for demo purposes
  const googleClientId =
    process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID";

  return (
    <div className="login-container">
      <div className="login-image-section">
        <img
          src={require("../../../assets/warehouse.jpeg")}
          alt="Wholesale management"
          className="login-bg-image"
        />
        <div className="login-overlay">
          <Typography variant="h4" className="overlay-text">
            Wholesale Management
          </Typography>
          <Typography variant="body1" className="overlay-subtext">
            Streamlining your business operations
          </Typography>
        </div>
      </div>

      <div className="login-form-section">
        <Paper elevation={3} className="login-paper">
          <Box className="login-header">
            <Typography variant="h4" className="login-title">
              Vyavsaay ERP
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className="login-subtitle">
              Sign in with your Google account
            </Typography>
          </Box>

          <Divider className="login-divider" />

          {isLoading ? (
            <Box className="flex-center-box">
              <CircularProgress color="primary" />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Authenticating...
              </Typography>
            </Box>
          ) : isApproved === false ? (
            <Box className="flex-center-box">
              <Typography
                variant="body1"
                color="error"
                sx={{ fontWeight: 500, mb: 2 }}>
                Your access request is pending approval by admin.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsApproved(null)}
                className="login-button">
                Try Again
              </Button>
            </Box>
          ) : (
            <Box className="login-form">
              <TextField
                fullWidth
                margin="normal"
                label="Firm Name"
                name="firm"
                placeholder="Enter your firm name if applicable"
                value={userDetails.firm}
                onChange={handleInputChange}
                variant="outlined"
                helperText="Optional"
              />

              <TextField
                fullWidth
                margin="normal"
                label="Phone Number"
                name="phone"
                placeholder="Enter your phone number if applicable"
                value={userDetails.phone}
                onChange={handleInputChange}
                variant="outlined"
                helperText="Optional"
              />

              <Box sx={{ mt: 3, mb: 2 }} className="flex-center-box">
                <GoogleOAuthProvider clientId={googleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={handleGoogleLoginError}
                    useOneTap
                    theme="filled_blue"
                    shape="pill"
                    text="continue_with"
                    size="large"
                  />
                </GoogleOAuthProvider>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: "center" }} className="auth-info-box">
            <Typography variant="body2" color="textSecondary">
              This authentication is for the Vyavsaay ERP demo.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              For access, please contact the administrator.
            </Typography>
          </Box>
        </Paper>
      </div>
    </div>
  );
};

export default GoogleAuthScreen;
